import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Check, X, Ban, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Loader, PageHeader } from "@/components/cast/Common";

function DeleteButton({ label, testId, onConfirm }: { label: string; testId: string; onConfirm: () => void }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={testId} className="shrink-0 rounded-full text-red-600"><Trash2 className="mr-1 h-4 w-4" />Delete</Button></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete {label}?</AlertDialogTitle><AlertDialogDescription>This permanently removes it for everyone, along with its comments or discussions. It can't be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel><AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PlusAccess() {
  const [email, setEmail] = useState("");
  const go = async () => {
    try { const { data } = await api.post("/admin/plus", { email }); toast.success(data.plus ? `${data.email} now has Cast Plus` : `Cast Plus removed from ${data.email}`); setEmail(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-warm mb-6 flex flex-wrap items-center gap-3 p-5" data-testid="admin-plus-access">
      <div className="min-w-[200px] flex-1"><p className="font-semibold text-slate-900">Cast Plus access</p><p className="text-sm text-slate-500">Give or remove Plus features (like reading plans) for a member by email, until memberships launch.</p></div>
      <input data-testid="admin-plus-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@email.com" className="h-10 rounded-full border border-[#E2DBCE] bg-white px-4 text-sm" />
      <Button data-testid="admin-plus-toggle" disabled={!email.includes("@")} onClick={go} className="rounded-full bg-[#1E293B]">Toggle Plus</Button>
    </div>
  );
}


function ClearTestContent({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clear-test-content");
      toast.success(`Removed ${data.testimonies} testimonies, ${data.groups} groups, ${data.children} child profiles and ${data.accounts} test accounts`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-warm mb-8 flex flex-wrap items-center justify-between gap-4 p-5" data-testid="admin-clear-test">
      <div><p className="font-semibold text-slate-900">Clear test content</p><p className="text-sm text-slate-500">Removes testimonies, groups and child profiles starting with TEST_, plus test accounts (test…@example.com) and everything they posted.</p></div>
      <AlertDialog>
        <AlertDialogTrigger asChild><Button disabled={busy} data-testid="admin-clear-test-button" className="rounded-full bg-red-600 text-white hover:bg-red-700"><Trash2 className="mr-1 h-4 w-4" />{busy ? "Clearing…" : "Clear test content"}</Button></AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Clear all test content?</AlertDialogTitle><AlertDialogDescription>Everything starting with TEST_ and all test…@example.com accounts will be permanently removed. Real members and your admin account are not touched.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="admin-clear-test-cancel">Cancel</AlertDialogCancel><AlertDialogAction data-testid="admin-clear-test-confirm" onClick={run} className="bg-red-600 hover:bg-red-700">Clear it</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ContentManager({ onChange }: { onChange: () => void }) {
  const [c, setC] = useState<any>(null);
  const load = () => api.get("/admin/content").then((r) => setC(r.data));
  useEffect(() => { load(); }, []);
  if (!c) return <Loader />;
  const del = async (url: string, msg: string) => { try { await api.delete(url); toast.success(msg); load(); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
    <PlusAccess />
    <ClearTestContent onDone={() => { load(); onChange(); }} />
    <div className="grid gap-8 lg:grid-cols-2">
      <section className="space-y-3" data-testid="admin-content-testimonies">
        <h3 className="font-display text-2xl font-semibold">Testimonies ({c.testimonies.length})</h3>
        {c.testimonies.length === 0 ? <Empty title="No testimonies" /> : c.testimonies.map((t: any) => (
          <div key={t.id} className="card-warm flex items-start justify-between gap-4 p-4" data-testid={`admin-content-testimony-${t.id}`}>
            <div className="min-w-0"><p className="text-xs text-slate-500"><b className="text-slate-700">{t.author.username}</b> · {timeAgo(t.created_at)}{t.status !== "published" && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{t.status === "pending" ? "held" : t.status}</span>}</p><p className="mt-1 line-clamp-3 text-sm text-slate-800">{t.text}</p></div>
            <DeleteButton label="this testimony" testId={`admin-delete-testimony-${t.id}`} onConfirm={() => del(`/admin/testimonies/${t.id}`, "Testimony deleted")} />
          </div>
        ))}
      </section>
      <section className="space-y-3" data-testid="admin-content-groups">
        <h3 className="font-display text-2xl font-semibold">Study groups ({c.groups.length})</h3>
        {c.groups.length === 0 ? <Empty title="No groups" /> : c.groups.map((g: any) => (
          <div key={g.id} className="card-warm flex items-start justify-between gap-4 p-4" data-testid={`admin-content-group-${g.id}`}>
            <div className="min-w-0"><p className="font-semibold text-slate-900">{g.name}</p><p className="text-xs text-slate-500">Led by {g.author.username} · {g.members} member{g.members === 1 ? "" : "s"}</p></div>
            <DeleteButton label={`"${g.name}"`} testId={`admin-delete-group-${g.id}`} onConfirm={() => del(`/admin/groups/${g.id}`, "Group deleted")} />
          </div>
        ))}
      </section>
    </div>
    </>
  );
}

const Flags = ({ list }: { list: string[] }) => <div className="flex flex-wrap gap-1.5">{list.map((f) => <span key={f} className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">{f.replace("_", " ")}</span>)}</div>;

export default function Admin() {
  const { user } = useAuth();
  const [q, setQ] = useState<any>(null);
  const [m, setM] = useState<any>(null);
  const load = () => { api.get("/admin/queue").then((r) => setQ(r.data)); api.get("/admin/metrics").then((r) => setM(r.data)); };
  useEffect(() => { if (user.role === "admin") load(); }, [user.role]);
  if (user.role !== "admin") return <Navigate to="/shore" replace />;
  if (!q || !m) return <Loader />;
  const act = async (url: string, action: string) => { try { await api.post(url, { action }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const ban = async (uid: string) => { try { const { data } = await api.post(`/admin/users/${uid}/ban`); toast.success(data.banned ? "Account suspended" : "Account restored"); load(); } catch (e) { toast.error(errMsg(e)); } };

  const METRICS = [["users", "Members"], ["signups_7d", "Signups (7d)"], ["active_7d", "Active (7d)"], ["testimonies", "Testimonies"], ["story_completions", "Story completions"], ["child_profiles", "Child profiles"], ["groups", "Groups"], ["open_reports", "Open reports"]];
  return (
    <div>
      <PageHeader metaphor="Harbor Watch" label="Moderation" title="Keep the waters calm." subtitle="Review held posts, sensitive stories, and member reports." />
      <div className="mb-10 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-metrics">
        {METRICS.map(([k, l]) => <div key={k} className="card-warm p-5"><p className="font-display text-4xl font-semibold" data-testid={`metric-${k}`}>{m[k]}</p><p className="text-sm text-slate-500">{l}</p></div>)}
      </div>
      <Tabs defaultValue="testimonies">
        <TabsList className="mb-6 bg-[#F3ECE0]">
          <TabsTrigger value="testimonies" data-testid="admin-tab-testimonies">Testimonies ({q.testimonies.length})</TabsTrigger>
          <TabsTrigger value="comments" data-testid="admin-tab-comments">Comments ({q.comments.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports ({q.reports.length})</TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Manage content</TabsTrigger>
        </TabsList>
        <TabsContent value="testimonies" className="space-y-4">
          {q.testimonies.length === 0 ? <Empty title="Nothing to review" /> : q.testimonies.map((t: any) => (
            <div key={t.id} className="card-warm p-6" data-testid={`admin-testimony-${t.id}`}>
              <div className="flex flex-wrap items-center gap-3 text-sm"><b>{t.author.username}</b><span className="text-slate-500">{timeAgo(t.created_at)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{t.status === "pending" ? "held" : "live · needs review"}</span>
                <Flags list={[...t.flags, ...t.sensitive_topics]} /></div>
              <p className="mt-3 whitespace-pre-wrap text-slate-800">{t.text}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" data-testid={`admin-approve-post-button-${t.id}`} onClick={() => act(`/admin/testimonies/${t.id}`, "approve")} className="rounded-full bg-[#5B7065]"><Check className="mr-1 h-4 w-4" />Approve</Button>
                <Button size="sm" variant="outline" data-testid={`admin-reject-post-button-${t.id}`} onClick={() => act(`/admin/testimonies/${t.id}`, "reject")} className="rounded-full"><X className="mr-1 h-4 w-4" />Reject</Button>
                <Button size="sm" variant="ghost" data-testid={`admin-ban-author-${t.id}`} onClick={() => ban(t.author_id)} className="rounded-full text-red-600"><Ban className="mr-1 h-4 w-4" />Suspend author</Button>
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="comments" className="space-y-4">
          {q.comments.length === 0 ? <Empty title="No held comments" /> : q.comments.map((c: any) => (
            <div key={c.id} className="card-warm p-5" data-testid={`admin-comment-${c.id}`}>
              <div className="flex items-center gap-3 text-sm"><b>{c.author.username}</b><Flags list={c.flags} /></div>
              <p className="mt-2 text-slate-800">{c.text}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" data-testid={`admin-approve-comment-${c.id}`} onClick={() => act(`/admin/comments/${c.id}`, "approve")} className="rounded-full bg-[#5B7065]">Approve</Button>
                <Button size="sm" variant="outline" data-testid={`admin-reject-comment-${c.id}`} onClick={() => act(`/admin/comments/${c.id}`, "reject")} className="rounded-full">Reject</Button>
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="reports" className="space-y-4">
          {q.reports.length === 0 ? <Empty title="No open reports" /> : q.reports.map((r: any) => (
            <div key={r.id} className="card-warm p-5" data-testid={`admin-report-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3 text-sm"><span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">{r.reason}</span><span className="text-slate-500">{r.target_type} · reported by {r.author.username} · {timeAgo(r.created_at)}</span></div>
              <p className="mt-2 rounded-lg bg-[#FAF7F2] p-3 text-slate-800">{r.snippet || "(content unavailable)"}</p>
              {r.details && <p className="mt-2 text-sm text-slate-600">“{r.details}”</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" data-testid={`admin-report-remove-${r.id}`} onClick={() => act(`/admin/reports/${r.id}`, "remove")} className="rounded-full bg-red-600 hover:bg-red-700"><Trash2 className="mr-1 h-4 w-4" />Remove content</Button>
                <Button size="sm" variant="outline" data-testid={`admin-report-dismiss-${r.id}`} onClick={() => act(`/admin/reports/${r.id}`, "dismiss")} className="rounded-full">Dismiss</Button>
                {r.target_author_id && <Button size="sm" variant="ghost" data-testid={`admin-report-ban-${r.id}`} onClick={() => ban(r.target_author_id)} className="rounded-full text-red-600"><Ban className="mr-1 h-4 w-4" />Suspend author</Button>}
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="content"><ContentManager onChange={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
